// Sales targets — one net-sales amount per branch per month, optionally split
// per salesman (the user who raises the sale). A year grid on the
// administration kit: branches down the side, months across; a branch opens
// into its salesman rows. Edits stay local until Save, which sends only the
// changed cells (SalesTarget/SaveBulk; an empty / 0 cell removes the target).
//
// The dashboards read these: GetSummary `target`, SalesTarget/GetAll `achieved`.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { setheadertoggle } from "../../redux/toggle";
import { copySalesTargets, getSalesTargets, saveSalesTargets } from "../../services/SalesTargetApiServices";
import { getUserbyBranch } from "../../services/CoreApiServices";
import {
  AdminScreen,
  AxDrawer,
  AxField,
  AxFilterSelect,
  AxRefreshIcon,
  AxStatusKeys,
} from "../../components/adminsitration/ui";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmt = (n) => (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const lc = (g) => String(g || "").toLowerCase();
const keyOf = (branchID, userID, period) => `${lc(branchID)}|${lc(userID)}|${period}`;
const periodOf = (d) => dayjs(d).startOf("month").format("YYYY-MM-DD");
const errMsg = (e, fallback) => e?.response?.data?.message || e?.response?.data?.Message || fallback;

const SalesTarget = () => {
  const dispatch = useDispatch();
  const { BranchByUser } = useSelector((state) => state.userBranch);
  const { userId } = useSelector((state) => state.userauth);
  const { menuItems } = useSelector((state) => state.amc);
  const { togglehead } = useSelector((state) => state.headerToggle);

  const [year, setYear] = useState(dayjs().year());
  const [q, setQ] = useState("");
  const [server, setServer] = useState({}); // key -> target row
  const [edits, setEdits] = useState({}); // key -> typed text
  const [open, setOpen] = useState({}); // branch guid -> salesman rows shown
  const [users, setUsers] = useState({}); // branch guid -> [{ guid, name }]
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copy, setCopy] = useState(null); // { from, to, overwrite }

  // No "Sales Target" menu row is seeded yet; until it is, allow everything.
  const permit = useMemo(() => {
    const row = menuItems?.menuPermissions?.find?.((e) => e.menu?.name === "Sales Target");
    return row || { view: true, add: true, edit: true, deletion: true };
  }, [menuItems]);
  const canEdit = !!(permit.add || permit.edit);

  const branches = useMemo(
    () =>
      (Array.isArray(BranchByUser) ? BranchByUser : [])
        .map((o) => o?.branch)
        .filter((b) => b && b.guid),
    [BranchByUser]
  );
  const branchKey = branches.map((b) => b.guid).join("|");
  const shown = branches.filter((b) => !q || String(b.name || "").toLowerCase().includes(q.toLowerCase()));
  const periods = useMemo(() => MON.map((_, i) => dayjs(new Date(year, i, 1)).format("YYYY-MM-DD")), [year]);

  /* ------------------------------------------------------------ data */
  const load = useCallback(async () => {
    if (!branches.length) return;
    setLoading(true);
    try {
      const body = { branchIDs: branches.map((b) => b.guid), fromPeriod: periods[0], toPeriod: periods[11] };
      const [b, u] = await Promise.all([
        getSalesTargets({ ...body, level: "branch" }),
        getSalesTargets({ ...body, level: "user" }),
      ]);
      const m = {};
      [...(Array.isArray(b) ? b : []), ...(Array.isArray(u) ? u : [])].forEach((r) => {
        m[keyOf(r.branchID, r.userID, periodOf(r.period))] = r;
      });
      setServer(m);
    } catch (e) {
      console.error("Error loading sales targets:", e);
      toast.error(errMsg(e, "Could not load the sales targets"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchKey, periods]);

  useEffect(() => {
    load();
  }, [load]);

  // salesman rows: the branch's users, plus anyone who already has a target there
  const toggleBranch = async (b) => {
    const g = b.guid;
    setOpen((o) => ({ ...o, [g]: !o[g] }));
    if (users[g]) return;
    try {
      const list = await getUserbyBranch({ id: g });
      setUsers((u) => ({
        ...u,
        [g]: (Array.isArray(list) ? list : []).filter((x) => x?.guid).map((x) => ({ guid: x.guid, name: x.name || x.userName })),
      }));
    } catch (e) {
      setUsers((u) => ({ ...u, [g]: [] }));
      toast.error(errMsg(e, "Could not load the branch's users"));
    }
  };
  const salesmenOf = (g) => {
    const list = [...(users[g] || [])];
    Object.values(server).forEach((r) => {
      if (r.userID && lc(r.branchID) === lc(g) && !list.some((x) => lc(x.guid) === lc(r.userID)))
        list.push({ guid: r.userID, name: r.userName || "Salesman" });
    });
    return list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
  };

  /* ------------------------------------------------------------ edits */
  const valueOf = (k) => (k in edits ? edits[k] : server[k] ? String(Number(server[k].amount) || "") : "");
  const amountOf = (k) => Number(valueOf(k)) || 0;
  const dirtyKeys = Object.keys(edits).filter((k) => (Number(edits[k]) || 0) !== (Number(server[k]?.amount) || 0));
  const bad = Object.keys(edits).filter((k) => edits[k] !== "" && !(Number(edits[k]) >= 0));
  const setCell = (k, v) => setEdits((e) => ({ ...e, [k]: v }));

  useEffect(() => {
    if (!dirtyKeys.length) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirtyKeys.length]);

  const save = async () => {
    if (!dirtyKeys.length || saving) return;
    if (bad.length) return toast.error("Targets must be numbers of 0 or more");
    setSaving(true);
    try {
      const targets = dirtyKeys.map((k) => {
        const [branchID, userID, period] = k.split("|");
        const amount = Number(edits[k]) || 0;
        return { branchID, userID: userID || null, period, amount: amount || null };
      });
      const res = await saveSalesTargets({ targets, updatedUser: `{${userId?.id}}` });
      toast.success(`Saved ${res?.saved ?? targets.length} ${targets.length === 1 ? "target" : "targets"}`);
      setEdits({});
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not save the targets"));
    } finally {
      setSaving(false);
    }
  };
  const discard = () => setEdits({});

  // Ctrl+S saves
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const runCopy = async () => {
    if (!copy?.from || !copy?.to) return toast.error("Pick both months");
    if (copy.from === copy.to) return toast.error("Pick two different months");
    try {
      const res = await copySalesTargets({
        fromPeriod: `${copy.from}-01`,
        toPeriod: `${copy.to}-01`,
        overwrite: !!copy.overwrite,
        branchIDs: shown.map((b) => b.guid),
        updatedUser: `{${userId?.id}}`,
      });
      toast.success(`Copied ${res?.saved ?? 0} targets to ${dayjs(`${copy.to}-01`).format("MMM YYYY")}`);
      setCopy(null);
      if (Number(copy.to.slice(0, 4)) === year) load();
    } catch (e) {
      toast.error(errMsg(e, "Could not copy the targets"));
    }
  };

  /* ------------------------------------------------------------ render */
  const thisMonth = periodOf(dayjs());
  const cell = (branchID, userID, p) => {
    const k = keyOf(branchID, userID, p);
    const r = server[k];
    const dirty = dirtyKeys.includes(k);
    const pctDone = r && Number(r.amount) ? Number(r.achievedPct) || ((Number(r.achieved) || 0) / Number(r.amount)) * 100 : null;
    return (
      <td key={p} className={"axst-cell" + (p === thisMonth ? " now" : "")}>
        <input
          type="number"
          min="0"
          step="any"
          inputMode="decimal"
          className={(dirty ? "dirty" : "") + (bad.includes(k) ? " bad" : "")}
          value={valueOf(k)}
          disabled={!canEdit}
          placeholder="—"
          aria-label={`Target ${dayjs(p).format("MMM YYYY")}`}
          onChange={(e) => setCell(k, e.target.value)}
        />
        {pctDone !== null && !dirty ? (
          <span
            className={"axst-ach" + (pctDone >= 100 ? " good" : p < thisMonth && pctDone < 85 ? " low" : "")}
            title={`Achieved ${fmt(r.achieved)} of ${fmt(r.amount)} · to date ${fmt(r.targetToDate)}`}
          >
            {Math.round(pctDone)}%
          </span>
        ) : null}
      </td>
    );
  };
  const yearTotal = (branchID, userID) => periods.reduce((a, p) => a + amountOf(keyOf(branchID, userID, p)), 0);
  const years = [];
  for (let y = dayjs().year() + 1; y >= dayjs().year() - 4; y--) years.push({ value: y, label: String(y) });

  return (
    <AdminScreen
      title="Sales targets"
      subtitle="Monthly net-sales targets per branch, with an optional split per salesman"
      searchValue={q}
      onSearchChange={(e) => setQ(e.target.value)}
      onSearchClear={() => setQ("")}
      searchPlaceholder="Search branches"
      searchHint={null}
      split={false}
      filters={
        <AxFilterSelect
          value={year}
          onChange={(e) => {
            if (dirtyKeys.length && !window.confirm("Discard the unsaved targets?")) return;
            setEdits({});
            setYear(Number(e.target.value) || dayjs().year());
          }}
          placeholder="Year"
          options={years}
        />
      }
      tools={
        <>
          <button type="button" className="axa-iconbtn" title="Reload" onClick={load}>
            <AxRefreshIcon />
          </button>
          <button
            type="button"
            className="axa-iconbtn"
            aria-pressed={!!togglehead}
            title="Collapse the app header"
            onClick={() => dispatch(setheadertoggle(!togglehead))}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M4 10l4-4 4 4" />
            </svg>
          </button>
        </>
      }
      actions={
        canEdit ? (
          <>
            <button
              type="button"
              className="axa-btn axa-btn-ghost"
              onClick={() =>
                setCopy({
                  from: dayjs().subtract(1, "month").format("YYYY-MM"),
                  to: dayjs().format("YYYY-MM"),
                  overwrite: false,
                })
              }
            >
              Copy month
            </button>
            <button type="button" className="axa-btn axa-btn-ghost" onClick={discard} disabled={!dirtyKeys.length}>
              Discard
            </button>
            <button type="button" className="axa-btn axa-btn-primary" onClick={save} disabled={!dirtyKeys.length || saving}>
              {saving ? "Saving…" : dirtyKeys.length ? `Save ${dirtyKeys.length}` : "Save"}
            </button>
          </>
        ) : null
      }
      list={
        <div className="axa-tablewrap axst-wrap">
          <table className="axa-ut axst">
            <thead>
              <tr>
                <th className="axst-name">Branch / salesman</th>
                {periods.map((p, i) => (
                  <th key={p} className={"num" + (p === thisMonth ? " now" : "")}>
                    {MON[i]}
                  </th>
                ))}
                <th className="num">Year</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((b) => (
                <React.Fragment key={b.guid}>
                  <tr className="axst-branch">
                    <td className="axst-name">
                      <button type="button" className="axst-tog" aria-expanded={!!open[b.guid]} onClick={() => toggleBranch(b)}>
                        <span className="car">{open[b.guid] ? "▾" : "▸"}</span>
                        {b.name}
                      </button>
                    </td>
                    {periods.map((p) => cell(b.guid, "", p))}
                    <td className="num axst-total">{fmt(yearTotal(b.guid, ""))}</td>
                  </tr>
                  {open[b.guid] &&
                    (users[b.guid] ? (
                      salesmenOf(b.guid).length ? (
                        salesmenOf(b.guid).map((u) => (
                          <tr key={u.guid} className="axst-user">
                            <td className="axst-name">{u.name}</td>
                            {periods.map((p) => cell(b.guid, u.guid, p))}
                            <td className="num axst-total">{fmt(yearTotal(b.guid, u.guid))}</td>
                          </tr>
                        ))
                      ) : (
                        <tr className="axst-user">
                          <td className="axst-name" colSpan={periods.length + 2}>
                            No users are assigned to this branch.
                          </td>
                        </tr>
                      )
                    ) : (
                      <tr className="axst-user">
                        <td className="axst-name" colSpan={periods.length + 2}>
                          Loading salesmen…
                        </td>
                      </tr>
                    ))}
                </React.Fragment>
              ))}
              {!shown.length && (
                <tr>
                  <td colSpan={periods.length + 2} className="axst-empty">
                    {loading ? "Loading…" : branches.length ? "No branch matches the search." : "No branches are assigned to this user."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      }
      status={
        <AxStatusKeys
          keys={[
            { k: "Ctrl+S", t: "Save" },
            { k: "▸", t: "Salesman targets" },
          ]}
          right={
            dirtyKeys.length
              ? `${dirtyKeys.length} unsaved ${dirtyKeys.length === 1 ? "change" : "changes"}`
              : `${Object.keys(server).length} targets in ${year} · % = achieved, net sales with VAT`
          }
        />
      }
    >
      <AxDrawer
        open={!!copy}
        onClose={() => setCopy(null)}
        title="Copy a month's targets"
        subtitle={`${shown.length} ${shown.length === 1 ? "branch" : "branches"}, branch and salesman targets`}
        footer={
          <>
            <button type="button" className="axa-btn axa-btn-ghost" onClick={() => setCopy(null)}>
              Cancel
            </button>
            <button type="button" className="axa-btn axa-btn-primary" onClick={runCopy}>
              Copy
            </button>
          </>
        }
      >
        {copy ? (
          <div className="axst-copy">
            <AxField label="From month">
              <input type="month" value={copy.from} onChange={(e) => setCopy((c) => ({ ...c, from: e.target.value }))} />
            </AxField>
            <AxField label="To month">
              <input type="month" value={copy.to} onChange={(e) => setCopy((c) => ({ ...c, to: e.target.value }))} />
            </AxField>
            <label className="axst-check">
              <input
                type="checkbox"
                checked={!!copy.overwrite}
                onChange={(e) => setCopy((c) => ({ ...c, overwrite: e.target.checked }))}
              />
              Overwrite targets already set in the target month
            </label>
          </div>
        ) : null}
      </AxDrawer>
    </AdminScreen>
  );
};

export default SalesTarget;
